"use client";

import { useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { AuthCard, AuthField, AuthLink, AuthStatus, authFieldClassName, authPrimaryClass } from "@/components/auth/auth-card";
import { authClient } from "@/lib/auth-client";
import { emailSchema } from "@/modules/account/schemas";

export function ForgotPasswordForm() {
  const [done, setDone] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const form = useForm<{ email: string }>({
    resolver: zodResolver(emailSchema),
    defaultValues: { email: "" },
  });

  async function onSubmit(values: { email: string }) {
    setFormError(null);
    const result = await authClient.requestPasswordReset({
      email: values.email.trim().toLowerCase(),
      redirectTo: "/reset-password",
    });
    if (result.error) {
      setFormError("Не удалось отправить письмо. Попробуйте ещё раз чуть позже.");
      return;
    }
    setDone(true);
  }

  if (done) {
    return (
      <AuthCard
        title="Письмо отправлено"
        description="Если аккаунт с таким email существует, мы отправили ссылку для восстановления."
        footer={<AuthLink href="/login">← Вернуться ко входу</AuthLink>}
      >
        <p className="text-sm leading-6 text-[var(--m-muted)]" role="status">
          Ссылка действует ограниченное время.
        </p>
      </AuthCard>
    );
  }

  return (
    <AuthCard
      title="Восстановление пароля"
      description="Укажите email — мы отправим ссылку для создания нового пароля."
      footer={<AuthLink href="/login">← Вернуться ко входу</AuthLink>}
    >
      <form className="flex flex-col gap-4" onSubmit={form.handleSubmit(onSubmit)} noValidate>
        <AuthField label="Email" htmlFor="email" error={form.formState.errors.email?.message}>
          <input
            id="email"
            type="email"
            autoComplete="email"
            className={authFieldClassName}
            aria-invalid={Boolean(form.formState.errors.email) || undefined}
            aria-describedby={form.formState.errors.email ? "email-error" : undefined}
            {...form.register("email")}
          />
        </AuthField>
        {formError ? <AuthStatus tone="error">{formError}</AuthStatus> : null}
        <button type="submit" className={authPrimaryClass} disabled={form.formState.isSubmitting}>
          {form.formState.isSubmitting ? "Отправляем…" : "Отправить ссылку"}
        </button>
      </form>
    </AuthCard>
  );
}
